import { useState } from 'react'
import { Input } from '@/components/ui/input'
import { BudgetCheckIcon, CancelIcon, SavedIcon, SpinnerIcon } from '@/components/icons'
import { formatCurrencyAutoLocale } from '@/utils/tools'
import getSymbolFromCurrency from 'currency-symbol-map'

const BudgetInput = ({ adset, onUpdate, currency, minDailyBudget }) => {
    const [isEditing, setIsEditing] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [isSaved, setIsSaved] = useState(false)
    const [tempValue, setTempValue] = useState('')
    const [savedValue, setSavedValue] = useState('')
    const [error, setError] = useState('')
    const symbol = getSymbolFromCurrency(currency)

    const startEdit = () => {
        setIsEditing(true)
        setTempValue(adset.budget.toString())
        setError('')
        setIsSaved(false)
    }

    const cancelEdit = () => {
        setIsEditing(false)
        setTempValue('')
        setError('')
    }

    const confirmEdit = async () => {
        const value = parseInt(tempValue)

        if (isNaN(value) || value < 2) {
            const formattedMinAmount = formatCurrencyAutoLocale({
                amount: minDailyBudget / 100,
                currency,
                showCode: true,
            })
            setError(`Budget must be at least ${formattedMinAmount}`)
            return
        }

        setError('')
        setIsSaving(true)

        try {
            await onUpdate(adset.db_id, value)

            // Show saved state
            setIsSaving(false)
            setIsSaved(true)
            setIsEditing(false)
            setSavedValue(value.toString())
            setTempValue('')

            // Clear saved state after 2 seconds
            setTimeout(() => {
                setIsSaved(false)
                setSavedValue('')
            }, 2000)
        } catch (error) {
            setIsSaving(false)
            setError('Failed to update budget. Please try again.')
        }
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            confirmEdit()
        } else if (e.key === 'Escape') {
            cancelEdit()
        }
    }

    if (isSaving) {
        return (
            <div className="tw:flex tw:items-center tw:gap-2">
                <div className="tw:min-w-[140px] tw:max-w-[200px] tw:h-[32px] tw:bg-[#040D16] tw:border tw:border-[#3B5168] tw:rounded tw:px-3 tw:flex tw:items-center tw:justify-center tw:!text-[14px] tw:!font-normal tw:text-white tw:whitespace-nowrap tw:overflow-hidden tw:text-ellipsis">
                    {formatCurrencyAutoLocale({
                        amount: tempValue || 0,
                        currency,
                        showCode: true,
                    })}
                </div>
                <SpinnerIcon className="tw:w-6 tw:h-6" />
            </div>
        )
    }

    if (isSaved) {
        return (
            <div className="tw:flex tw:items-center tw:gap-2">
                <div className="tw:min-w-[140px] tw:max-w-[200px] tw:h-[32px] tw:bg-[#040D16] tw:border tw:border-[#3B5168] tw:rounded tw:px-3 tw:flex tw:items-center tw:justify-center tw:!text-[14px] tw:!font-normal tw:text-white tw:whitespace-nowrap tw:overflow-hidden tw:text-ellipsis">
                    {formatCurrencyAutoLocale({
                        amount: savedValue || adset.budget || 0,
                        currency,
                        showCode: true,
                    })}
                </div>
                <div className="tw:flex tw:items-center tw:gap-1">
                    <SavedIcon className="tw:w-6 tw:h-6" />
                </div>
            </div>
        )
    }

    if (isEditing) {
        return (
            <div className="tw:flex tw:items-center tw:gap-2">
                <div className="tw:relative">
                    <span className="tw:absolute tw:left-3 tw:top-1/2 tw:transform tw:-translate-y-1/2 tw:text-white tw:text-[14px] tw:z-10">
                        {symbol}
                    </span>
                    <Input
                        type="number"
                        step="1"
                        value={tempValue}
                        onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9]/g, '')
                            setTempValue(value)
                            setError('')
                        }}
                        className="tw:w-[140px] tw:h-[32px] tw:pl-7 tw:bg-[#040D16] tw:border tw:border-[#3871FF] tw:rounded tw:!text-[14px] tw:!font-normal tw:text-white tw:hover:border-[#2450BF] tw:focus:border-[#2450BF]"
                        autoFocus
                        onKeyDown={handleKeyDown}
                    />
                </div>
                <div className="tw:flex tw:items-center tw:gap-1">
                    <button
                        onClick={confirmEdit}
                        className="tw:p-1 tw:hover:bg-[#1c2737] tw:rounded tw:transition-colors"
                    >
                        <BudgetCheckIcon className="tw:w-6 tw:h-6" />
                    </button>
                    <button
                        onClick={cancelEdit}
                        className="tw:p-1 tw:hover:bg-[#1c2737] tw:rounded tw:transition-colors"
                    >
                        <CancelIcon className="tw:w-6 tw:h-6 tw:text-[#3871FF]" />
                    </button>
                </div>
                {error && (
                    <div className="tw:absolute tw:top-full tw:left-0 tw:mt-1 tw:bg-red-500 tw:text-white tw:px-2 tw:py-1 tw:rounded tw:text-xs tw:whitespace-nowrap tw:z-50">
                        {error}
                    </div>
                )}
            </div>
        )
    }

    return (
        <button
            onClick={startEdit}
            className="tw:min-w-[140px] tw:max-w-[200px] tw:h-[32px] tw:bg-[#040D16] tw:border tw:border-[#3B5168] tw:rounded tw:px-3 tw:!text-[14px] tw:!font-normal tw:text-white tw:text-center tw:outline-none tw:hover:border-[#58a6ff] tw:focus:border-[#58a6ff] tw:transition-colors tw:cursor-pointer tw:whitespace-nowrap tw:overflow-hidden tw:text-ellipsis"
        >
            {formatCurrencyAutoLocale({
                amount: adset.budget || 0,
                currency,
                showCode: true,
            })}
        </button>
    )
}

export default BudgetInput
